import { tradesBlotterApiConnector } from 'apiConnector/blotters';
import { isNavigatingInTradeBlotter, isNavigatingOutTradeBlotter } from 'epic/utils/navigation/navigation';
import { StateObservable } from 'redux-observable';
import {
  delay,
  filter,
  map,
  mergeWith,
  Observable,
  retryWhen,
  SchedulerLike,
  startWith,
  switchMap,
  takeUntil,
  timer,
  withLatestFrom,
} from 'rxjs';
import { Action } from 'state/actions';
import {
  tradesBlottersCreditCheckStatusRequested,
  tradesBlottersCreditCheckStatusUpdated,
} from 'state/blotters/trades/tradesBlotters.actions';
import { selectTradesBlottersType } from 'state/blotters/trades/tradesBlotters.selector';
import { BlotterType } from 'state/blotters/trades/types/types';
import { EpicWithThunk } from 'state/state.common';
import { AppState } from 'state/state.model';

const makeNavigatingInTradeBlotterWithAlllowedBlotterTypes =
  (allowedBlotters: BlotterType[]) => (actions$: Observable<Action>, state$: StateObservable<AppState>) => {
    const filterMathing$: Observable<Action> = actions$.pipe(
      filter(action => action.type === 'TRADES_BLOTTERS_FILTER_ENABLED' && allowedBlotters.includes(action.blotter)),
    );
    return actions$.pipe(
      filter(isNavigatingInTradeBlotter),
      withLatestFrom(state$),
      filter(([_, state]) => {
        const blotterType = selectTradesBlottersType(state);
        return allowedBlotters.includes(blotterType);
      }),
      map(([action, _state]) => action),
      mergeWith(filterMathing$),
    );
  };

const makeNavigatingOutTradeBlotterWithAlllowedBlotterTypes =
  (allowedBlotters: BlotterType[]) => (actions$: Observable<Action>, state$: StateObservable<AppState>) => {
    const filterMathing$: Observable<Action> = actions$.pipe(
      filter(action => action.type === 'TRADES_BLOTTERS_FILTER_ENABLED' && !allowedBlotters.includes(action.blotter)),
    );
    return actions$.pipe(
      filter(isNavigatingOutTradeBlotter),
      withLatestFrom(state$),
      filter(([_, state]) => {
        const blotterType = selectTradesBlottersType(state);
        return allowedBlotters.includes(blotterType);
      }),
      map(([action, _state]) => action),
      mergeWith(filterMathing$),
    );
  };

export function pollTradesBlottersCreditCheckEpic(scheduler?: SchedulerLike): EpicWithThunk {
  const blottersWithCreditCheck: BlotterType[] = ['products', 'trades'];
  const makeNavigationIn$ = makeNavigatingInTradeBlotterWithAlllowedBlotterTypes(blottersWithCreditCheck);
  const makeNavigationOut$ = makeNavigatingOutTradeBlotterWithAlllowedBlotterTypes(blottersWithCreditCheck);

  return (actions$, state$, { httpClient, selectors: { getIsUserInternal }, getSgmeConfiguration }) => {
    const navigationIn$: Observable<Action> = makeNavigationIn$(actions$, state$);
    const navigationOut$: Observable<Action> = makeNavigationOut$(actions$, state$);

    const pollingFrequency = getSgmeConfiguration().creditCheckStatusPollingIntervalMs ?? 1000 * 60 * 30;

    return navigationIn$.pipe(
      withLatestFrom(state$),
      filter(([, state]) => getIsUserInternal(state)),
      switchMap(() =>
        timer(0, pollingFrequency, scheduler).pipe(
          switchMap(() =>
            tradesBlotterApiConnector(httpClient)
              .fetchCreditCheckStatus()
              .pipe(
                map(result => tradesBlottersCreditCheckStatusUpdated(result)),
                startWith(tradesBlottersCreditCheckStatusRequested()),
                retryWhen(errors => errors.pipe(delay(15000))),
              ),
          ),
          takeUntil(navigationOut$),
        ),
      ),
    );
  };
}
